import {
  deleteSavedViews,
  getSavedViews,
  getSavedView,
  createView,
} from 'api/MessagingInsightsAPI';
import { BoldSpan } from 'modules/messagingInsights/Styled';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { addToRootSaga } from 'store';
import { addNegativeToast, addPositiveToast, addWarningToast } from 'modules/toastContainer';
import { ListedView } from '../types';
import * as actions from './actions';
import * as actionTypes from './actionTypes';
import {
  BulkDeleteViewsRequestAction,
  FetchViewsRequestAction,
  MakeACopyRequestAction,
} from './types';

export function* fetchViewsSaga({ queryParams }: FetchViewsRequestAction) {
  try {
    const { data } = yield call(getSavedViews, queryParams);
    yield put(actions.fetchViewsResponse(data));
  } catch {
    yield put(actions.fetchViewsError());
    yield put(addNegativeToast('Error fetching views, please try again.'));
  }
}

export function* deleteViewSaga(view: Partial<ListedView>, email: string) {
  if (view.createdByEmail === email) {
    try {
      yield call(deleteSavedViews, view.id);
      yield put(
        addPositiveToast(
          <>
            Saved view <BoldSpan>{view.name}</BoldSpan> deleted successfully.
          </>,
        ),
      );
    } catch (error) {
      yield put(
        addNegativeToast(
          <>
            Saved view <BoldSpan>{view.name}</BoldSpan> cannot be deleted.
          </>,
          { noDelay: true },
        ),
      );
    }
  } else {
    try {
      yield put(
        addWarningToast(
          <>
            Only the creator of <BoldSpan>{view.name}</BoldSpan> can delete it.
          </>,
          { noDelay: true },
        ),
      );
    } catch (e) {
      yield put(
        addNegativeToast(
          <>
            Saved view <BoldSpan>{view.name}</BoldSpan> cannot be deleted.
          </>,
          { noDelay: true },
        ),
      );
    }
  }
}

export function* bulkDeleteViewsSaga({
  deleteData,
  email,
  callback,
}: BulkDeleteViewsRequestAction) {
  try {
    yield all(deleteData.map((view) => call(deleteViewSaga, view, email)));
    callback();
  } catch {
    yield put(addNegativeToast("Some views couldn't be deleted."));
  }
}

export function* makeACopySaga({ viewId, isPrivate, callback }: MakeACopyRequestAction) {
  try {
    const { data } = yield call(getSavedView, viewId);
    try {
      const name = `[Clone of ${data.id}] ${data.name}`;
      delete data.id;
      delete data.creatorId;
      delete data.isProjectDefault;
      yield call(createView, { ...data, isPrivate, name });
      yield put(addPositiveToast('Successfully copied view.'));
      callback();
    } catch {
      yield put(addNegativeToast('Error copying view.'));
    }
  } catch {
    yield put(addNegativeToast('Error copying view.'));
  }
}

addToRootSaga([
  takeLatest(actionTypes.FETCH_VIEWS_REQUEST, fetchViewsSaga),
  takeLatest(actionTypes.BULK_DELETE_VIEWS_REQUEST, bulkDeleteViewsSaga),
  takeLatest(actionTypes.MAKE_A_COPY_REQUEST, makeACopySaga),
]);
